import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Copy, Check, Download, Mail, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import PublicShell, { useSiteLang } from "@/components/PublicShell";
import { api, errMsg, downloadFile, fmtDateTime, money } from "@/lib/api";

const R = {
  en: { copy: "Copy", copied: "Copied", allCopied: "All codes copied", title: "Thank you, your codes are ready", order: "Order", org: "Organization", codes: "Codes", paid: "Amount paid",
    yours: "Your screening codes", copyAll: "Copy all", pdf: "Download codes PDF", give: (d: string) => `Give each employee or volunteer one code. They'll use it to start their application. This page shows your codes until ${d}.`,
    hidden: "For security, your codes are no longer shown here.", issuing: "Your codes are being issued now.", emailed: (e: string) => `A copy of your codes (list + PDF) was emailed to ${e}.`, more: "Buy more codes", home: "Back to home" },
  es: { copy: "Copiar", copied: "Copiado", allCopied: "Todos los códigos copiados", title: "Gracias, sus códigos están listos", order: "Pedido", org: "Organización", codes: "Códigos", paid: "Monto pagado",
    yours: "Sus códigos para voluntarios", copyAll: "Copiar todos", pdf: "Descargar PDF de códigos", give: (d: string) => `Entregue un código a cada voluntario para que comience su solicitud. Esta página muestra sus códigos hasta el ${d}.`,
    hidden: "Por seguridad, sus códigos ya no se muestran aquí.", issuing: "Estamos emitiendo sus códigos ahora.", emailed: (e: string) => `Enviamos una copia de sus códigos (lista + PDF) a ${e}.`, more: "Comprar más códigos", home: "Volver al inicio" },
};

function CodeRow({ code, i, t }: { code: string; i: number; t: typeof R.en }) {
  const [done, setDone] = useState(false);
  const copy = async () => { await navigator.clipboard.writeText(code); setDone(true); setTimeout(() => setDone(false), 1500); };
  return (
    <li className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5" data-testid={`receipt-code-${i}`}>
      <span className="font-mono text-base font-bold tracking-wider text-[#1B365D]">{code}</span>
      <button className="btn-secondary btn-sm" onClick={copy} data-testid={`receipt-code-copy-${i}`}>{done ? <Check className="h-4 w-4 text-[#15803D]" /> : <Copy className="h-4 w-4" />} {done ? t.copied : t.copy}</button>
    </li>
  );
}

export default function OrderReceipt({ sid, fallback }: { sid: string; fallback: React.ReactNode }) {
  const [o, setO] = useState<any>(undefined);
  const lang = useSiteLang();
  const t = R[lang];
  useEffect(() => { api.get(`/payments/order/${sid}`).then((r) => setO(r.data)).catch(() => setO(null)); }, [sid]);
  if (o === undefined) return null;
  if (o === null) return <>{fallback}</>;
  const copyAll = async () => { await navigator.clipboard.writeText(o.codes.join("\n")); toast.success(t.allCopied); };
  return (
    <PublicShell>
      <div className="mx-auto max-w-2xl px-4 pt-16" lang={lang}>
        <div className="card-soft fade-up p-8 sm:p-10" data-testid="order-receipt">
          <div className="grid h-14 w-14 place-items-center rounded-3xl bg-[#D5F5EE]"><CheckCircle2 className="h-7 w-7 text-[#15803D]" /></div>
          <h1 className="mt-5 text-4xl font-extrabold tracking-tight">{t.title}</h1>
          <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4" data-testid="receipt-summary">
            <div><dt className="text-[#64748B]">{t.order}</dt><dd className="font-semibold" data-testid="receipt-order-number">{o.number}</dd></div>
            <div className="col-span-2 min-w-0 sm:col-span-1"><dt className="text-[#64748B]">{t.org}</dt><dd className="break-words font-semibold" data-testid="receipt-org">{o.org_name}</dd></div>
            <div><dt className="text-[#64748B]">{t.codes}</dt><dd className="font-semibold" data-testid="receipt-quantity">{o.quantity}</dd></div>
            <div><dt className="text-[#64748B]">{t.paid}</dt><dd className="font-semibold" data-testid="receipt-amount">{money(o.amount)}</dd></div>
          </dl>
          {o.codes_visible ? (
            <>
              <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-bold">{t.yours}</h2>
                <div className="flex gap-2">
                  <button className="btn-secondary btn-sm" onClick={copyAll} data-testid="receipt-copy-all-button"><Copy className="h-4 w-4" /> {t.copyAll}</button>
                  <button className="btn-primary btn-sm" data-testid="receipt-download-pdf-button" onClick={() => downloadFile(`/payments/order/${sid}/codes.pdf`, `screening-codes-${o.number}.pdf`).catch((e) => toast.error(errMsg(e)))}><Download className="h-4 w-4" /> {t.pdf}</button>
                </div>
              </div>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2" data-testid="receipt-code-list">{o.codes.map((c: string, i: number) => <CodeRow key={c} code={c} i={i} t={t} />)}</ul>
              <p className="mt-4 text-sm text-[#64748B]">{t.give(fmtDateTime(o.visible_until))}</p>
            </>
          ) : (
            <p className="mt-8 rounded-xl bg-[#F1EFEA] px-4 py-3 text-sm text-[#1A2536]" data-testid="receipt-codes-hidden">
              {o.status === "issued" ? t.hidden : t.issuing}
            </p>
          )}
          <p className="mt-4 flex items-start gap-2 text-sm text-[#475569]" data-testid="receipt-email-note"><Mail className="mt-0.5 h-4 w-4 shrink-0" /> {t.emailed(o.contact_email)}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/order" state={{ reorder: o.reorder }} className="btn-primary" data-testid="receipt-buy-more-button"><ShoppingCart className="h-4 w-4" /> {t.more}</Link>
            <Link to="/" className="btn-secondary" data-testid="payment-home-link">{t.home}</Link>
          </div>
        </div>
      </div>
    </PublicShell>
  );
}
